import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DollarSign, Plus, Trash2, Save, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBillingSettings = () => {
  const [defaultMultiplier, setDefaultMultiplier] = useState('1.5');
  const [rows, setRows] = useState([]);
  const [knownModels, setKnownModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [bRes, mRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/billing-settings`),
          axios.get(`${API_URL}/api/admin/openrouter-models`).catch(() => ({ data: [] })),
        ]);
        setDefaultMultiplier(String(bRes.data.default_multiplier));
        setRows((bRes.data.model_multipliers || []).map((m) => ({
          model: m.model,
          multiplier: String(m.multiplier),
        })));
        // Live OpenRouter catalog + models already seen locally (logs / agents)
        const orModels = (mRes.data || []).map((m) => ({ id: m.id, name: m.name || m.id }));
        const orIds = new Set(orModels.map((m) => m.id));
        const localOnly = (bRes.data.known_models || [])
          .filter((id) => !orIds.has(id))
          .map((id) => ({ id, name: `${id}（本地記錄）` }));
        setKnownModels([...orModels, ...localOnly].sort((a, b) => a.id.localeCompare(b.id)));
      } catch {
        toast.error('載入計費設定失敗');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        default_multiplier: parseFloat(defaultMultiplier),
        model_multipliers: rows
          .filter((r) => r.model.trim())
          .map((r) => ({ model: r.model.trim(), multiplier: parseFloat(r.multiplier) })),
      };
      const r = await axios.post(`${API_URL}/api/admin/billing-settings`, payload);
      toast.success(r.data.message || '已儲存，即時生效');
    } catch (e) {
      toast.error(e.response?.data?.detail || '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const updateRow = (i, key, val) => {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  };

  const exampleCredits = (mult) => {
    const m = parseFloat(mult);
    if (!m || m <= 0) return '—';
    return (0.05 * m / 0.01).toFixed(1);
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-neutral-400 text-sm">載入中...</p>
      </div>
    );
  }

  return (
    <>
      <header className="bg-white border-b border-neutral-200 px-4 md:px-8 py-4 md:py-6 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg md:text-2xl font-heading font-bold text-neutral-900">計費設定</h1>
            <p className="text-xs md:text-sm text-neutral-500 mt-0.5">設定各模型的利潤倍數 · 儲存後即時生效，無需重啟</p>
          </div>
          <Button onClick={save} disabled={saving} className="rounded-full" data-testid="billing-save-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? '儲存中...' : '儲存設定'}
          </Button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-3xl space-y-6">

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-primary-600" />
              <h3 className="font-semibold text-sm text-neutral-900">預設利潤倍數</h3>
            </div>
            <p className="text-xs text-neutral-500 mb-4">
              未在下方模型表中指定的模型，一律按此倍數收費。用戶扣點 = 實際 API 成本 × 倍數 ÷ $0.01
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                step="0.1"
                min="0.1"
                max="100"
                value={defaultMultiplier}
                onChange={(e) => setDefaultMultiplier(e.target.value)}
                className="w-32"
                data-testid="billing-default-multiplier-input"
              />
              <span className="text-xs text-neutral-400">
                例：成本 $0.05 → 收 {exampleCredits(defaultMultiplier)} 點
              </span>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold text-sm text-neutral-900">各模型倍數（優先於預設）</h3>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full text-xs"
                onClick={() => setRows((prev) => [...prev, { model: '', multiplier: '1.5' }])}
                data-testid="billing-add-model-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> 新增模型
              </Button>
            </div>
            <p className="text-xs text-neutral-500 mb-4 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              模型清單即時取自 OpenRouter（共 {knownModels.length} 個，輸入關鍵字即可篩選）。名稱需與扣點記錄完全一致，同一模型的不同版本需逐個設定。
            </p>

            <datalist id="billing-known-models">
              {knownModels.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </datalist>

            {rows.length === 0 && (
              <p className="text-xs text-neutral-400 text-center py-6 border border-dashed border-neutral-200 rounded-lg">
                尚未設定任何模型倍數 — 所有模型均按預設倍數收費
              </p>
            )}

            <div className="space-y-2">
              {rows.map((row, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`billing-model-row-${i}`}>
                  <Input
                    list="billing-known-models"
                    placeholder="例：anthropic/claude-opus-4.7"
                    value={row.model}
                    onChange={(e) => updateRow(i, 'model', e.target.value)}
                    className="flex-1 text-xs"
                    data-testid={`billing-model-row-${i}-model`}
                  />
                  <span className="text-xs text-neutral-400">×</span>
                  <Input
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="100"
                    value={row.multiplier}
                    onChange={(e) => updateRow(i, 'multiplier', e.target.value)}
                    className="w-24 text-xs"
                    data-testid={`billing-model-row-${i}-multiplier`}
                  />
                  <span className="text-[10px] text-neutral-400 w-28 hidden md:block">
                    $0.05 → {exampleCredits(row.multiplier)} 點
                  </span>
                  <button
                    type="button"
                    onClick={() => setRows((prev) => prev.filter((_, idx) => idx !== i))}
                    className="p-1.5 rounded-md text-neutral-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    data-testid={`billing-model-row-${i}-remove`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </Card>

        </div>
      </main>
    </>
  );
};

export default AdminBillingSettings;
